import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Select, { MultiValue, StylesConfig } from 'react-select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import ErrorMessage from '@/components/ErrorMessage';
import { useSignupOtherDetailsForm } from '@/redux/auth/useAuthForm';
import { IUserSignupDataWithConfirmPassword } from '@/pages/Signup/signup.types';
import { useGetCNCBrandsQuery } from '@/redux/cncBrand/cncBrand.api.slice';
import { CNCBrandI } from '@/redux/cncBrand/cncBrand';
import { useLazyGetCNCModelsQuery } from '@/redux/cncModel/cncModel.api.slice';
import { useLazyGetCNCVariantsQuery } from '@/redux/cncVariant/cncVariant.api.slice';

type SelectOption = { label: string; value: string };

// Theme react-select to match the brand green + the rest of the form inputs.
const selectStyles: StylesConfig<SelectOption, true> = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    borderColor: state.isFocused ? '#16a34a' : 'hsl(214.3 31.8% 91.4%)',
    boxShadow: state.isFocused ? '0 0 0 1px #16a34a' : 'none',
    '&:hover': { borderColor: '#16a34a' },
  }),
  placeholder: (base) => ({ ...base, color: 'hsl(215.4 16.3% 46.9%)' }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#dcfce7',
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({ ...base, color: '#166534' }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#166534',
    '&:hover': { backgroundColor: '#bbf7d0', color: '#14532d' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#dcfce7' : 'white',
    color: '#1f2937',
  }),
};

function OtherDetails({
  formData,
  errors,
  setFormData,
}: {
  formData: IUserSignupDataWithConfirmPassword;
  errors: { [key: string]: string };
  setFormData: Dispatch<SetStateAction<IUserSignupDataWithConfirmPassword>>;
}) {
  const signupBaiscDetailsForm = useSignupOtherDetailsForm();
  const { data: cncBrandsData, isLoading: isLoadingCNCBrands } =
    useGetCNCBrandsQuery();
  const [getCNCModels, { isFetching: isLoadingCNCModels }] =
    useLazyGetCNCModelsQuery();
  const [getCNCVariants, { isFetching: isLoadingCNCVariants }] =
    useLazyGetCNCVariantsQuery();

  const [cncModelOptions, setCncModelOptions] = useState<SelectOption[]>([]);

  const [cncVariantOptions, setCncVariantOptions] = useState<SelectOption[]>(
    []
  );

  // Pre-select the "ONEFINITY CNC" brand once brands load.
  useEffect(() => {
    if (!cncBrandsData || formData.cncBrand.length > 0) return;
    // Prefix match, not an exact one: the brand has been named both "Onefinity"
    // and "Onefinity CNC", and an exact match silently stops defaulting.
    // Same matcher as sortByOnefinity and the add-machine dialog.
    const onefinity = cncBrandsData.find((b) =>
      b.name.trim().toLowerCase().startsWith('onefinity')
    );
    if (!onefinity) return;
    setFormData((prev) => ({ ...prev, cncBrand: [onefinity._id] }));
    // Warm the model select so the user sees a populated list, not a focus-to-load delay.
    getCNCModels([onefinity._id])
      .unwrap()
      .then((models) =>
        setCncModelOptions(models.map((m) => ({ label: m.name, value: m._id })))
      )
      .catch(() => {});
  }, [cncBrandsData, formData.cncBrand.length, getCNCModels, setFormData]);

  const getOptionsFromCncBrands = (cncBrands: CNCBrandI[] | undefined) => {
    if (!cncBrands) return [];
    const options = cncBrands.map((brand) => ({
      label: brand.name,
      value: brand._id,
    }));
    return options;
  };

  const getOptionsFromCncVariants = async () => {
    const cncVariants = await getCNCVariants({
      modelIdList: formData.cncModel,
    }).unwrap();
    const options = cncVariants.map((variant) => ({
      label: variant.name,
      value: variant._id,
    }));
    setCncVariantOptions(options);
  };

  const handleCncBrandChange = (
    newValue: MultiValue<{ label: string; value: string }>
  ) => {
    const cncBrands = newValue.map((brand) => brand.value);

    setFormData((prev) => ({
      ...prev,
      cncBrand: cncBrands,
      cncModel: [],
      cncVariant: [],
    }));
  };

  const handleCncModelChange = (option: MultiValue<SelectOption>) => {
    setFormData((prev) => ({
      ...prev,
      cncModel: option
        .map((model) => model?.value)
        .filter((value): value is string => !!value),
      cncVariant: [],
    }));
  };

  const handleCncVariantChange = (newValue: MultiValue<SelectOption>) => {
    const cncVariants = newValue
      .map((variant) => variant?.value)
      .filter((value): value is string => !!value);
    setFormData((prev) => ({ ...prev, cncVariant: cncVariants }));
  };

  const getOptionsFromCncModels = async () => {
    const cncModels = await getCNCModels(formData.cncBrand).unwrap();
    const options = cncModels.map((model) => ({
      label: model.name,
      value: model._id,
    }));
    setCncModelOptions(options);
  };

  return (
    <Form {...signupBaiscDetailsForm}>
      <form className="space-y-5">
        <FormField
          control={signupBaiscDetailsForm.control}
          name="cncBrand"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CNC brand</FormLabel>
              <FormControl>
                <Select
                  styles={selectStyles}
                  placeholder="Select your brand(s)"
                  options={getOptionsFromCncBrands(cncBrandsData)}
                  onChange={(newValue) => handleCncBrandChange(newValue)}
                  isLoading={isLoadingCNCBrands}
                  isDisabled={isLoadingCNCBrands}
                  loadingMessage={() => 'Loading brands…'}
                  isSearchable
                  isMulti
                  name={field.name}
                  value={formData.cncBrand
                    .map((brandId) => {
                      const brand = cncBrandsData?.find(
                        (b) => b._id === brandId
                      );
                      return brand
                        ? { label: brand.name, value: brand._id }
                        : null;
                    })
                    .filter(
                      (option): option is SelectOption => option !== null
                    )}
                />
              </FormControl>
              <ErrorMessage error={errors.cncBrand} />
            </FormItem>
          )}
        />
        <FormField
          control={signupBaiscDetailsForm.control}
          name="cncModel"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CNC model</FormLabel>
              <FormControl>
                <Select
                  styles={selectStyles}
                  placeholder="Select your model(s)"
                  options={cncModelOptions}
                  onChange={(option) => handleCncModelChange(option)}
                  isLoading={isLoadingCNCModels}
                  isDisabled={
                    isLoadingCNCBrands || formData.cncBrand.length === 0
                  }
                  loadingMessage={() => 'Loading models…'}
                  noOptionsMessage={() =>
                    formData.cncBrand.length === 0
                      ? 'Select a brand first'
                      : 'No models available'
                  }
                  isSearchable
                  isMulti
                  onFocus={() => {
                    getOptionsFromCncModels();
                  }}
                  value={formData?.cncModel
                    .map((modelId) => {
                      const model = cncModelOptions?.find(
                        (m) => m.value === modelId
                      );
                      return model
                        ? { label: model.label, value: model.value }
                        : null;
                    })
                    .filter(
                      (option): option is SelectOption => option !== null
                    )}
                  name={field.name}
                />
              </FormControl>
              <ErrorMessage error={errors.cncModel} />
            </FormItem>
          )}
        />
        <FormField
          control={signupBaiscDetailsForm.control}
          name="cncVariant"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CNC variant</FormLabel>
              <FormControl>
                <Select
                  styles={selectStyles}
                  placeholder="Select your variant(s)"
                  options={cncVariantOptions}
                  onChange={(newValue) => handleCncVariantChange(newValue)}
                  isLoading={isLoadingCNCVariants}
                  isDisabled={formData.cncModel.length === 0}
                  loadingMessage={() => 'Loading variants…'}
                  noOptionsMessage={() =>
                    formData.cncModel.length === 0
                      ? 'Select a model first'
                      : 'No variants available'
                  }
                  isSearchable
                  isMulti
                  onFocus={() => {
                    getOptionsFromCncVariants();
                  }}
                  value={formData?.cncVariant
                    .map((variantId) => {
                      const variant = cncVariantOptions?.find(
                        (v) => v.value === variantId
                      );
                      return variant
                        ? { label: variant.label, value: variant.value }
                        : null;
                    })
                    .filter(
                      (option): option is SelectOption => option !== null
                    )}
                  name={field.name}
                />
              </FormControl>
              <ErrorMessage error={errors.cncVariant} />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}

export default OtherDetails;
